import { gql, useMutation } from '@apollo/client';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import React, { useEffect } from 'react';

import { variantRouteConfig } from 'src/app/graph/routes';
import { Loading } from 'src/components/common/loading/Loading';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { ChannelCardChannelFragment } from '../channelCard/ChannelCard';

export const createBuildSubscriptionMutation = gql`
  mutation CreateBuildSubscriptionMutation(
    $graphId: ID!
    $channelId: ID!
    $variant: String!
  ) {
    service(id: $graphId) {
      createCompositionStatusSubscription(
        channelID: $channelId
        variant: $variant
      ) {
        id
        channels {
          id
          ...ChannelCardChannelFragment
        }
      }
    }
  }
  ${ChannelCardChannelFragment}
`;

export interface CreateBuildSubscriptionState {
  variant?: string;
  targetChannel?: GraphQLTypes.AddNotificationWizardChannelFragment;
}

interface Props {
  isOpen: boolean;
  state: Readonly<CreateBuildSubscriptionState> | null;
  onSubscriptionCreated: (newSubscriptionId: string) => void;
  onClose: () => void;
}

export function CreateBuildSubscriptionModal({
  isOpen,
  state,
  onSubscriptionCreated,
  onClose,
}: Props) {
  const { graphId } = useRouteParams(variantRouteConfig);
  const [createBuildSubscription, { data, loading, error }] = useMutation<
    GraphQLTypes.CreateBuildSubscriptionMutation,
    GraphQLTypes.CreateBuildSubscriptionMutationVariables
  >(createBuildSubscriptionMutation);

  const targetChannelId = state?.targetChannel?.id;
  const newSubId = data?.service?.createCompositionStatusSubscription.id;
  useEffect(() => {
    if (
      !state?.variant ||
      !targetChannelId ||
      !graphId ||
      loading ||
      newSubId ||
      error
    ) {
      return;
    }
    const createSubscription = async () => {
      const subscription = await createBuildSubscription({
        variables: {
          graphId,
          channelId: targetChannelId,
          // state.variant is already checked for undefined in the
          // if clause so technically we shouldn't have to fallback to 'current'
          variant: state.variant ?? 'current',
        },
      });
      const newSubscriptionId =
        subscription.data?.service?.createCompositionStatusSubscription.id;
      if (subscription && newSubscriptionId) {
        onSubscriptionCreated(newSubscriptionId);
      }
    };
    createSubscription();
  }, [
    state,
    targetChannelId,
    graphId,
    loading,
    newSubId,
    error,
    createBuildSubscription,
    onSubscriptionCreated,
  ]);
  return (
    <Modal isOpen={isOpen} size="lg" onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>
            {loading ? 'Adding notification...' : 'Something went wrong'}
          </ModalTitle>
        </ModalHeader>
        <ModalBody>
          {loading ? <Loading /> : error || !newSubId ? <ErrorMessage /> : null}
        </ModalBody>
        <ModalFooter>
          <Button variant="primary" isDisabled={loading} onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
